import { Injectable } from '@angular/core';
import { SESSION_STORAGE_KEY } from '@core/configs/local-storage-key.config';
import { SessionStorageService } from './session-storage.service';

export interface SelectedOfficeIdentity {
    corpCd?: string;
    officeCd?: string;
    companyId?: number | string;
    organizationId?: number | string;
}

@Injectable({
    providedIn: 'root'
})
export class SessionContextService {
    constructor(
        private readonly sessionStorageService: SessionStorageService
    ) { }

    getStaffInfo<T = any>(): T | null {
        return this.sessionStorageService.getSessionData<T>(SESSION_STORAGE_KEY.staffInfo);
    }

    getCorpPrefix(): string {
        const staffInfo = this.getStaffInfo<any>();
        return staffInfo?.corpPrefix || '';
    }

    setSelectedOfficeIdentity(office: any): void {
        if (!office) return;
        this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.officeSelected, office);
        this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.selectedOfficeIdentity, {
            corpCd: office.corpCd,
            officeCd: office.officeCd,
            companyId: office.companyId,
            organizationId: office.organizationId
        });
    }

    getSelectedOfficeIdentity(): SelectedOfficeIdentity | null {
        const identity = this.sessionStorageService.getSessionData<SelectedOfficeIdentity>(SESSION_STORAGE_KEY.selectedOfficeIdentity);
        if (identity) return identity;

        const legacyOfficeSelected = this.sessionStorageService.getSessionData<any>(SESSION_STORAGE_KEY.officeSelected);
        const legacyIdentity = this.toSelectedOfficeIdentity(legacyOfficeSelected);
        if (legacyIdentity) {
            this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.selectedOfficeIdentity, legacyIdentity);
        }

        return legacyIdentity;
    }

    getOfficeSelected<T = any>(): T | null {
        const staffInfo = this.getStaffInfo<any>();
        const identity = this.getSelectedOfficeIdentity();
        const belongs = staffInfo?.stfBelongs || [];

        if (identity && belongs.length > 0) {
            const office = belongs.find((item: any) => this.matchesOfficeIdentity(item, identity));
            if (office) return office as T;
        }

        return this.sessionStorageService.getSessionData<T>(SESSION_STORAGE_KEY.officeSelected);
    }

    getOfficeCd(): string {
        return this.getOfficeSelected<any>()?.officeCd || '';
    }

    getCorpCd(): string {
        return this.getOfficeSelected<any>()?.corpCd || '';
    }

    getOfficeType(): number | null {
        return this.getOfficeSelected<any>()?.officeType ?? null;
    }

    getStaffCd(): string {
        return this.getStaffInfo<any>()?.stfCd || '';
    }

    private matchesOfficeIdentity(office: any, identity: SelectedOfficeIdentity): boolean {
        return String(office?.corpCd ?? '') === String(identity.corpCd ?? '')
            && String(office?.officeCd ?? '') === String(identity.officeCd ?? '')
            && String(office?.companyId ?? '') === String(identity.companyId ?? '')
            && String(office?.organizationId ?? '') === String(identity.organizationId ?? '');
    }

    private toSelectedOfficeIdentity(office: any): SelectedOfficeIdentity | null {
        if (!office?.corpCd || !office?.officeCd) return null;

        return {
            corpCd: office.corpCd,
            officeCd: office.officeCd,
            companyId: office.companyId,
            organizationId: office.organizationId
        };
    }
}
